import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Briefcase } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";
import { useLang, fmtMoney } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const JobPortal = () => {
  const lang = useLang();
  const [jobs, setJobs] = useState(null);
  const [country, setCountry] = useState("");
  const [skill, setSkill] = useState("");
  const [level, setLevel] = useState("");
  const [applying, setApplying] = useState(null);

  const load = () => {
    const params = new URLSearchParams({ ...(country && { country }), ...(level && { level }) });
    axios.get(`${API}/jobs`, { ...headers(), params }).then((r) => setJobs(r.data.jobs)).catch(() => setJobs([]));
  };
  useEffect(load, [country, level]);

  const apply = async (job) => {
    setApplying(job.id);
    try {
      await axios.post(`${API}/jobs/${job.id}/apply`, {}, headers());
      toast.success(`Applied to ${job.title} ✅`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(null);
    }
  };

  if (jobs === null) return null;
  const filtered = jobs.filter((j) => !skill || j.skills.some((s) => s.toLowerCase().includes(skill.toLowerCase())));
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="job-portal">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><Briefcase className="h-5 w-5 text-[#06B6D4]" /> Career Job Portal</p>
      <p className="mt-1 text-xs text-slate-500">AI match scores based on your skills, level and country.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <select data-testid="jp-country" value={country} onChange={(e) => setCountry(e.target.value)}
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
          <option value="">All countries</option>
          <option value="India">India</option>
          <option value="UAE">UAE</option>
          <option value="Global">Global</option>
        </select>
        <select data-testid="jp-level" value={level} onChange={(e) => setLevel(e.target.value)}
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white outline-none focus:border-[#7C3AED]/60">
          <option value="">All levels</option>
          <option value="fresher">Fresher</option>
          <option value="junior">Junior</option>
          <option value="mid">Mid</option>
          <option value="senior">Senior</option>
        </select>
        <input data-testid="jp-skill" value={skill} onChange={(e) => setSkill(e.target.value)} placeholder="Skill (e.g. culinary)"
          className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
      </div>
      {filtered.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No jobs match those filters yet.</p>
      ) : (
        <div className="mt-4 space-y-2.5">
          {filtered.map((j) => (
            <div key={j.id} data-testid={`job-${j.id.slice(-6)}`} className="rounded-2xl border border-white/10 bg-[#09090B]/80 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold">{j.title}</p>
                  <p className="mt-0.5 font-jbmono text-[10px] text-slate-500">{j.company} · {j.country} · {j.level}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                  j.match_score >= 70 ? "bg-[#22C55E]/10 text-[#22C55E]" : j.match_score >= 40 ? "bg-[#F5B300]/10 text-[#F5B300]" : "bg-white/5 text-slate-500"
                }`} data-testid={`job-match-${j.id.slice(-6)}`}>{j.match_score}% match</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {j.skills.map((s) => (
                  <span key={s} className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 font-jbmono text-[9px] text-slate-400">{s}</span>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-jbmono text-xs font-bold text-[#22C55E]">{j.salary}</span>
                <button data-testid={`job-apply-${j.id.slice(-6)}`} onClick={() => apply(j)} disabled={applying === j.id}
                  className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-4 py-1.5 font-display text-[10px] font-bold uppercase text-white disabled:opacity-50">
                  {applying === j.id ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Apply"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
